import { CreateComponentPublicInstance } from 'vue';
import { ISisenseApiResponse } from '../interfaces';
import { getDataFromSisense } from '../services/sisense';
import app from '@/main';
import { TFilterCheckboxConfig, TFilterConfig } from '@/components/RsSidebarFilters.vue';

/**
 * In production, this is RPM's parent account client ID.
 */
export const RPM_CLIENT_ID = 1337;
// export const RPM_ENV: IEnvironment['VUE_APP_ENV'] = 'production';

/**
 * Custom RPM filters (their account setup requires a reporting hack for getting filterable data)
 */
export const rpmFilters = {
  rpmProperties: {
    source: 'Leads v2',
    requestDataConfig: {
      metadata: [
        {
          dim: '[custom_filter_properties.property_id]',
        },
        {
          dim: '[custom_filter_properties.property_name]',
          sort: 'asc',
        },
        {
          dim: '[custom_filter_properties.client_id]',
          filter: {
            explicit: true,
            multiSelection: false,
            members: [ RPM_CLIENT_ID ],
          },
        },
      ],
      count: 500,
    },
  },

  rpmCities: {
    source: 'Leads v2',
    requestDataConfig: {
      metadata: [
        {
          dim: '[custom_filter_cities.city_id]',
        },
        {
          dim: '[custom_filter_cities.city_name]',
          sort: 'asc',
        },
        {
          dim: '[custom_filter_cities.client_id]',
          filter: {
            explicit: true,
            multiSelection: false,
            members: [ RPM_CLIENT_ID ],
          },
        },
      ],
      count: 500,
    },
  },

  rpmPropertyTypes: {
    source: 'Leads v2',
    requestDataConfig: {
      metadata: [
        {
          dim: '[custom_filter_property_types.building_type_id]',
        },
        {
          dim: '[custom_filter_property_types.building_type]',
          sort: 'asc',
        },
        {
          dim: '[custom_filter_property_types.client_id]',
          filter: {
            explicit: true,
            multiSelection: false,
            members: [ RPM_CLIENT_ID ],
          },
        },
      ],
      count: 500,
    },
  },

  rpmSources: {
    source: 'Leads v2',
    requestDataConfig: {
      metadata: [
        {
          dim: '[custom_filter_sources.partner_id]',
        },
        {
          dim: '[custom_filter_sources.partner_name]',
          sort: 'asc',
        },
        {
          dim: '[custom_filter_sources.client_id]',
          filter: {
            explicit: true,
            multiSelection: false,
            members: [ RPM_CLIENT_ID ],
          },
        },
      ],
      count: 500,
    },
  },

  rpmWebsites: {
    source: 'Leads v2',
    requestDataConfig: {
      metadata: [
        {
          dim: '[custom_filter_websites.website_id]',
          filter: {
            explicit: true,
            multiSelection: false,
            exclude: {
              members: [ -1 ],
            },
          },
        },
        {
          dim: '[custom_filter_websites.website_name]',
          sort: 'asc',
        },
        {
          dim: '[custom_filter_websites.client_id]',
          filter: {
            explicit: true,
            multiSelection: false,
            members: [ RPM_CLIENT_ID ],
          },
        },
      ],
      count: 500,
    },
  },

  rpmOwnershipGroups: {
    source: 'Leads v2',
    requestDataConfig: {
      metadata: [
        {
          dim: '[custom_filter_ownership_groups.ownership_group_id]',
        },
        {
          dim: '[custom_filter_ownership_groups.ownership_group_name]',
          sort: 'asc',
        },
        {
          dim: '[custom_filter_ownership_groups.client_id]',
          filter: {
            explicit: true,
            multiSelection: false,
            members: [ RPM_CLIENT_ID ],
          },
        },
      ],
      count: 500,
    },
  },
};

/**
 * Rather than going to our APIs to fetch data for filtering, we've made custom models on Sisense to fetch what we need.
 * @param key
 * @returns
 */
export const getRPMLookupData = async (key: keyof typeof rpmFilters) => {
  try {
    const response = await getDataFromSisense<ISisenseApiResponse>(
      'post',
      `api/datasources/${rpmFilters[key].source}/jaql`,
      rpmFilters[key].requestDataConfig,
      true,
    );

    if (response?.error) {
      throw new Error(`${response.database}: ${response.details}`);
    }

    return response?.values?.map((record: [0, '1']) => ({ id: record[0], name: record[1] })) ?? [];
  } catch (err) {
    console.error(err);

    app()?.$eventBus.emit('show-error-toast', {
      message: 'Please refresh.',
      title: `Error Retrieving ${rpmFilters[key].source}`,
    });

    return [];
  }
};

/**
 * A filter wrapper for the RsSidebarFilter component's configurable `fetcher` property.
 * This is where we override where filter options are being fetched from. If our current client is RPM,
 * use the Sisense models. Else, use our regular APIs to fetch filter options.
 * @param vm Vue app instance
 * @param rpmFilterKey Which key to fetch filters for in `rpmFilters` object.
 * @param originalFetcher The standard fetcher to run if the current client is not RPM.
 * @returns `TFilterConfig['fetcher']
 */
export const checkForRPMFetcher = (
  vm: CreateComponentPublicInstance,
  rpmFilterKey: keyof typeof rpmFilters,
  originalFetcher: (TFilterCheckboxConfig | TFilterCheckboxConfig)['fetcher'],
): (TFilterCheckboxConfig | TFilterCheckboxConfig)['fetcher'] => {
  if (vm.$rsGlobals.$currentClient.id === RPM_CLIENT_ID) {
    return async () => {
      try {
        const data = await getRPMLookupData(rpmFilterKey);
        return data.map((record) => ({ text: record.name, value: `${record.id}` })) ?? [];
      } catch (err) {
        console.error(err);
        return [];
      }
    };
  }

  return originalFetcher;
};
